import moment from 'moment'
import { 
  DiaDaFolhaDados, 
  DiaDaFolha, 
  ehDiaDaFolhaCompleto,
  Nota,
  SaldoDiario,
  Marcacao,
  Apontamento as ApontamentoNovo,
  JornadaSobreaviso,
  Abono
} from './linha.interface'
import { ArbitraryTm, Line, Empregado, EmpregadoId, WorkHoursBank, WorkHourBank } from '@/tipos'
import { gerarChaveLinhaRecalculo, jornadaPrecisaSerGerada, resolverSeEhDiaDeTrabalho } from './folha.utils'
import { LegendaDaJornada } from '@/typescript/modulos/governanca/solicitacoes/dominio/solicitacoes.enum';
import store from '@/store'

export interface TransformarDadosResultado {
  empregados: Empregado[];
  linhas: Line[];
  inicioPeriodo: string | null;
  fimPeriodo: string | null;
}

export const mapearPontoDaNovaApi = (ponto: Marcacao) => ({
  id: ponto.id,
  // A API nova envia o ID da correção em `correcao_id` para uma marcação
  // real anulada. O fluxo legado usa `fake_id` para montar
  // DELETE /api/fake-pt/{id}; sem este mapeamento ele usa o ID do ponto.
  fake_id: ponto.correcao_id || undefined,
  isAc: ponto.criado_por_correcao_automatica,
  isAutocomplete: ponto.eh_autocompletar,
  time: ponto.hora_marcacao,
  justification: ponto.justificativa,
  timeLogKind: ponto.momento_da_jornada,
  pt: ponto.marcacao_id || ponto.ponto,
  processado: ponto.processado,
  kind: ponto.tipo_do_ponto,
  anulado: ponto.anulado,
  createdByIa: ponto.criado_por_ia,
  ponto_manual_sobrescrito: ponto.ponto_manual_sobrescrito
})

const mapearAbono = (
  abono: Abono | null,
  apontamentos: ReadonlyArray<ApontamentoNovo>
): Line['arbTms'] => {
  if (!abono) return null

  const apontamentosPorNome = new Map(
    apontamentos.map(apontamento => [apontamento.nome_padrao, apontamento])
  )

  const arbitraryTms: Record<string, ArbitraryTm> = {}

  for (const alteracao of abono.alteracoes) {
    if (alteracao.silencioso) continue

    const apontamentosComMesmoValor = apontamentos.filter(
      ({ valor }) => valor === alteracao.valor
    )

    const apontamentoPorValor =
      apontamentosComMesmoValor.length === 1
        ? apontamentosComMesmoValor[0]
        : undefined

    const apontamento =
      apontamentosPorNome.get(alteracao.nome_apontamento) ??
      apontamentoPorValor

    if (!apontamento) continue

    arbitraryTms[alteracao.nome_apontamento] = {
      value: alteracao.valor,
      text: `${apontamento.nome_customizado_abreviado} .Abn`,
      hover: `${apontamento.nome_customizado} Abonados`
    }
  }

  return {
    arbitraryTms,
    arbitraryTmsId: abono.id,
    arbitraryApproved: abono.aprovado
  }
}

const linhaBase: Partial<Line> = {
  id: null,
  hash: null,
  employee: undefined,
  data: '',
  timelogs: [[], [], [], []],
  forceNoExtra: false,
  forceNonPause: false,
  toBeRecalculated: false,
  customRulesUsedName: null,
  company: null,
  lockRequest: null,
  justify: null,
  dayJourney: {
    id: '',
    'manual_change': false,
    day: '',
    journey: {
      id: '',
      name: '',
      active: true,
      code: '',
      kind: 0,
      caracteristicaDaFolga: '',
      color: '',
      lglDura: 0,
      start: '',
      stPause: '',
      endPause: '',
      end: '',
      pausasDescanso: '',
      justify: false,
      suspend: false,
      mandatoryPresence: false,
      customRules: null,
      payKind: '',
      leaveJourney: false,
      vacationJourney: false,
      lockerJourney: null,
      deleted: false
    } as any,
    onCallJourney: null,
    'forced_journey': null,
    locked: false,
    lockedAt: null,
    employeeId: '',
    isHoliday: false,
    isWorkDay: false,
    isLeave: false,
    isVacation: false,
    falta: false,
    toBeRecalculated: false,
    trabalhoRemoto: false
  },
  timeMeasurements: {
    apontamentos: {
      obs: {
        value: '',
        text: 'Observações',
        hover: 'Observações'
      }
    }
  },
  workHoursBank: {
    rule: {},
    workHourBank: {},
    workHourCloser: []
  },
  infoToRefetch: {
    employee: '',
    data: '',
    hashFront: '',
    numberDuplicated: 0,
    id: null
  },
  diaVerificado: false
}

export const transformarDadosDaNovaApiParaAntiga = (
  novosDados: DiaDaFolhaDados[],
  storeContext: any = store
): TransformarDadosResultado => {
  try {
    const lines: Line[] = []
    const employeesMap: Record<string, Empregado> = {}
    const inicioPeriodo = novosDados[0]?.dia || null
    const fimPeriodo = novosDados[novosDados.length - 1]?.dia || null

    const mapearApontamentos = (diaLinha: any, apontamentos: ReadonlyArray<ApontamentoNovo>) => {
      const apontamentosObj: any = {
        obs: {
          value: diaLinha.observacao_da_linha || "",
          text: "Observações",
          hover: "Observações"
        }
      }

      if (Array.isArray(apontamentos)) {
        apontamentos.forEach(function (apontamento) {
          if (!apontamento) {
            return
          }

          apontamentosObj[apontamento.nome_padrao] = {
            extra_time_50: apontamento.nome_padrao,
            hover: apontamento.nome_customizado,
            text: apontamento.nome_customizado_abreviado,
            value: apontamento.valor,
            exibir_pdf: apontamento.exibir_pdf,
            exibir_folha: apontamento.exibir_folha,
          }
        })
      }

      return apontamentosObj
    }

    const mapearSaldosDiarios = (saldosDiarios: ReadonlyArray<SaldoDiario>): WorkHoursBank => {
      let workHourBank: any = {}
      const workHourCloser: any[] = []
      const regraDeBhStore = storeContext?.getters?.['sheet/regraDeBhDoEmpregado'] || (storeContext?.state?.sheet as any)?.regraDeBhDoEmpregado
      const rule: any = regraDeBhStore?.name ? {
        name: regraDeBhStore.name,
        id: regraDeBhStore.id,
        mudaNomenclaturaParaCompensacaoNaFolha: regraDeBhStore.mudaNomenclaturaParaCompensacaoNaFolha || regraDeBhStore.muda_nomenclatura_para_compensacao_na_folha || false
      } : {}


      if (!Array.isArray(saldosDiarios) || saldosDiarios.length === 0) {
        return {
          rule,
          workHourBank: workHourBank as WorkHourBank,
          workHourCloser
        }
      }

      saldosDiarios.forEach(saldo => {
        const saldoFormatado = {
          id: saldo.id,
          active: saldo.ativo,
          obs: saldo.descricao,
          isArbitrary: saldo.eh_banco_manual,
          isCompensation: saldo.eh_compensacao,
          isCompensated: saldo.esta_compensado,
          event: saldo.eh_evento,
          isApproved: saldo.esta_aprovado,
          allTimes: saldo.valor,
          expired: saldo.esta_vencido
        }

        if (saldo.eh_banco_manual) {
          workHourCloser.push(saldoFormatado)
        } else {
          workHourBank = saldoFormatado
        }
      })

      return {
        rule,
        workHourBank: workHourBank as WorkHourBank,
        workHourCloser
      }
    }

    const mapearTravamento = (dia: DiaDaFolha) => {
      if (!dia.travamento?.estado_travamento || !dia.travamento?.folha_travada) {
        return null
      }

      return { 
        id: null,
        empUnlockSolicited: false,
        timesheetLockedAt: dia.travamento.horario_travamento,
        unilateralLocker: false,
        state: dia.travamento.estado_travamento,
        prettyState: { 
          text: "", // preenchido no componente Lock.vue
          state: dia.travamento.estado_travamento
        },
        isAnInitialState: false,
        observation: ""
      }
    }

    const mapearNotas = (notas: ReadonlyArray<Nota> | null, empregadoId: EmpregadoId) => {
      if (!Array.isArray(notas)) {
        return null
      }

      const notasDoEmpregado = notas.filter(nota => nota.empregado === empregadoId)

      if (!notasDoEmpregado.length) {
        return null
      }

      return notasDoEmpregado.map(nota => ({
        id: nota.id,
        text: nota.texto,
        date: nota.dia,
        employeeId: nota.empregado,
        ownerId: nota.dono,
        lastUpdate: nota.ultima_atualizacao
      }))
    }

    const mapearJornadasDeSobreaviso = (jornadasDeSobreaviso: ReadonlyArray<JornadaSobreaviso> | null) => {
      if (!Array.isArray(jornadasDeSobreaviso) || jornadasDeSobreaviso.length === 0) {
        return null
      }

      return jornadasDeSobreaviso
        .filter((sobreaviso: JornadaSobreaviso) => !moment(sobreaviso.inicio).isSame(sobreaviso.fim))
        .map((sobreaviso: JornadaSobreaviso) => ({
          id: sobreaviso.id,
          startTime: sobreaviso.inicio,
          endTime: sobreaviso.fim,
          kind: sobreaviso.tipo,
          color: sobreaviso.cor
        }));
    }

    // para cada linha na nova api
    novosDados.forEach(dia => {

      // antes de mapear, verificar se é uma linha tem dados, ou seja, se é uma linha incompleta, ela não foi recalculada ainda na nova api
      // e consequentemente, não existe na tabela do back. Nesse caso, preenche a linha e deixa ela em estado de loading devido toBeRecalculated true

      // OBSERVAÇÃO: para linhas antes da data de admissão de um empregado, não é necessário recalcular
      // ou seja, a API retorna precisa_recalcular: false e exibimos linha com estado "Sem jornada"
      if (!ehDiaDaFolhaCompleto(dia)) {
        lines.push({
          ...linhaBase,
          toBeRecalculated: dia.precisa_recalcular ?? true,
          data: dia.dia,
          employee: {
            id: dia.empregado_id,
            name: '',
            closingDate: 1,
            company: [],
            autoPauseFill: false,
            code: '',
            active: true,
            fired: false,
            funct: '',
            customRules: {}
          } as any,
          dayJourney: {
            ...linhaBase.dayJourney,
            toBeRecalculated: dia.precisa_recalcular ?? true,
            journey: {
              ...linhaBase.dayJourney?.journey,
              name: dia.jornada?.nome_da_jornada || '',
              color: dia.jornada?.cor_da_jornada || ''
            },
          },
          infoToRefetch: {
            employee: dia.empregado_id,
            data: dia.dia,
            hashFront: '',
            numberDuplicated: 0,
            id: null
          },
          chaveRecalculo: gerarChaveLinhaRecalculo(dia.empregado_id, dia.dia),
          jornadaPrecisaSerGerada: jornadaPrecisaSerGerada(dia)
        } as Line)
        return
      }

      if (!employeesMap[dia.empregado_id]) {
        employeesMap[dia.empregado_id] = {
          id: dia.empregado_id,
          name: dia.nome_empregado,
          closingDate: 1, 
          company: [],
          autoPauseFill: false,
          code: '',
          active: true,
          fired: false,
          funct: '',
          customRules: {}
        } as any
      }

      const ehFeriado = dia.jornada_diaria.eh_feriado;
      const ehFerias = dia.jornada_diaria.eh_ferias;
      const ehAfastamento = dia.jornada_diaria.eh_afastamento;

      // se for 0 é dia de trabalho
      let kind = 0;
      if (ehFeriado || ehFerias || ehAfastamento || dia.jornada.nome_da_jornada === LegendaDaJornada.FOLGA) {
          kind = -1;
      }

      const oldLine: Line = {
        id: dia.linha.id_linha as string,
        hash: null,
        employee: employeesMap[dia.empregado_id],
        data: dia.dia,
        timelogs: [[], [], [], []] as any, // populamos depois
        forceNoExtra: dia.linha.desconsiderar_horas_extras_de_feriado || false,
        forceNonPause: dia.linha.desconsiderar_pausa || false,
        toBeRecalculated: dia.precisa_recalcular,
        customRulesUsedName: dia.nome_da_regra_de_calculo_usada,
        company: (() => {
          const empregado = (storeContext?.state?.common?.employees?.items || []).find((e: any) => e.id === dia.empregado_id)
          return empregado?.company?.[0]?.id || null
        })(),
        lockRequest: mapearTravamento(dia),
        justify: dia.justificativa ?? null,
        comments: mapearNotas(dia.notas, dia.empregado_id),
        isDoubleLine: false, 
        isFakeLine: false,
        dayJourney: {
          id: dia.jornada_diaria.id_jornada_diaria as string,
          'manual_change': dia.jornada_diaria.alterada_manualmente,
          day: dia.dia,
          journey: {
            id: '',
            kind: kind,
            start: dia.jornada.inicio_jornada,
            end: dia.jornada.fim_jornada,
            stPause: dia.jornada.inicio_pausa,
            endPause: dia.jornada.fim_pausa,
            color: dia.jornada.cor_da_jornada,
            name: dia.jornada.nome_da_jornada,
          } as any,
          onCallJourney: mapearJornadasDeSobreaviso(dia.jornada.jornadas_de_sobreaviso),
          'forced_journey': null,
          locked: dia.travamento?.folha_travada || false,
          lockedAt: null,
          employeeId: dia.empregado_id,
          isHoliday: dia.jornada_diaria.eh_feriado,
          isWorkDay: resolverSeEhDiaDeTrabalho(dia.jornada_diaria),
          isLeave: dia.jornada_diaria.eh_afastamento,
          isVacation: dia.jornada_diaria.eh_ferias,
          falta: dia.jornada_diaria.falta,
          toBeRecalculated: dia.precisa_recalcular,
          trabalhoRemoto: dia.jornada_diaria.trabalho_remoto,
        },
        timeMeasurements: {
          apontamentos: mapearApontamentos(dia.linha, dia.apontamentos)
        },
        arbTms: mapearAbono(dia.abono, dia.apontamentos),
        workHoursBank: mapearSaldosDiarios(dia.saldos_diarios) as any,
        infoToRefetch: { // apenas para o front não reclamar, não utilizado no novo fluxo
          employee: dia.empregado_id,
          data: dia.dia,
          hashFront: '', 
          numberDuplicated: 0,
          id: dia.linha.id_linha
        },
        regraDeAprovacao: { idRegraDeConversaoBh: null },
        chaveRecalculo: gerarChaveLinhaRecalculo(dia.empregado_id, dia.dia),
        diaVerificado: dia.dia_verificado,
        // A mutation `atualizarMultiplasLinhas` faz merge com a linha antiga: sem o
        // `false` explícito, o dia gerado manteria a flag do recálculo anterior.
        jornadaPrecisaSerGerada: false
      }

      if (dia.linha.pontos) {
        dia.linha.pontos.forEach((pontosArray, index) => {
          if (!Array.isArray(pontosArray)) return
          pontosArray.forEach(ponto => {
            (oldLine.timelogs as any)[index].push(mapearPontoDaNovaApi(ponto))
          })
        })
      }

      lines.push(oldLine)
    })

    return {
      empregados: Object.values(employeesMap),
      linhas: lines.sort((a, b) => moment(a.data).diff(moment(b.data))),
      inicioPeriodo,
      fimPeriodo
    }
  } catch {
    return {
      empregados: [],
      linhas: [],
      inicioPeriodo: null,
      fimPeriodo: null
    }
  }
}
